// resources/js/services/MapLayerServices.js
import { unByKey } from 'ol/Observable';
import TileLayer from 'ol/layer/Tile';
import WebGLTile from 'ol/layer/WebGLTile';
import TileWMS from "ol/source/TileWMS";
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import WMTS, { optionsFromCapabilities } from 'ol/source/WMTS';
import Swal from "sweetalert2";
import MainStore from "../store/MainStore";
import { CORSANYWHERE } from '../constants';
import axios from "axios"; // Ensure axios is imported
import _ from "lodash"; // Ensure lodash is imported if used
import $ from "jquery"; // Assuming jQuery is used for DOM manipulation
import { removeFeatureAction, resetVectorRemoveFeatureAction, replaceParcelles } from '../store/actions/geoJsonActions';
import MapGeoJsonService from './MapGeoJsonService';
import {hasWorkingWebGL} from '../components/utilities/webgl'

class MapLayerServices {

    constructor(useWebGL = false) { // Accept the flag
        this.useWebGL = hasWorkingWebGL();
        this.wmtsCapabilities = null;
        this.filteredCadastreLayers = [];
        this.layersFetched = false;
        this.layersFetching = false;
    }

    async loadWMTSCapabilities() {
        if (this.wmtsCapabilities) return;

        try {
            const response = await axios.get(MainStore.getState().AppReducer.geoserverApiUrl.replace(/\/wms$/, '/gwc/service/wmts?SERVICE=WMTS&REQUEST=GetCapabilities&VERSION=1.0.0'));
            const parser = new WMTSCapabilities();
            this.wmtsCapabilities = parser.read(response.data);
        } catch (error) {
            console.error("Failed to fetch WMTS capabilities:", error);
        }
    }

    getWMTSOptions(layerName) {
        try {
            const options = optionsFromCapabilities(this.wmtsCapabilities, {
                layer: layerName,
                matrixSet: 'EPSG:900913', // important to match this with GS
            });
            return options;
        } catch (e) {
            console.warn(`WMTS options not found for ${layerName}`);
            return null;
        }
    }

    /**
     * Fetches all layers from the API and caches the ones with filtered=true.
     * This method ensures layers are fetched only once.
     */
    async fetchFilteredLayers() {
        if (this.layersFetched) {
            return this.filteredCadastreLayers;
        }

        // Prevent multiple concurrent fetches
        if (this.layersFetching) {
            // Wait until the current fetch is complete
            await new Promise(resolve => {
                const check = setInterval(() => {
                    if (!this.layersFetching) {
                        clearInterval(check);
                        resolve();
                    }
                }, 100);
            });
            return this.filteredCadastreLayers;
        }

        this.layersFetching = true;

        try {
            const response = await axios.get('/Api/layers/all');
            if (response.data && response.data.data) {
                // Filter layers where 'filtered' is true
                this.filteredCadastreLayers = response.data.data
                    .filter(layer => layer.filtered)
                    .map(layer => layer.combined_slug);
                //console.log('Filtered Cadastre Layers:', this.filteredCadastreLayers); // Added logging
                this.layersFetched = true;
            } else {
                console.error("Invalid response structure from /Api/layers/all");
            }
        } catch (error) {
            console.error("Error fetching layers from /Api/layers/all:", error);
        } finally {
            this.layersFetching = false;
        }

        return this.filteredCadastreLayers;
    }

    /**
     * Removes a feature by ID from the vector layer.
     * @param {Map} map - The OpenLayers map instance.
     * @param {string} featureId - The ID of the feature to remove.
     */
    removeFeature(map, featureId) {
        //console.log(`MapLayerServices: Removing feature "${featureId}"`);
        const vectorLayer = map.getLayers().getArray().find(layer => layer.get('name') === 'vector-parcelle'); // Adjust the name accordingly

        if (vectorLayer) {
            const vectorSource = vectorLayer.getSource();
            const feature = vectorSource.getFeatureById(featureId);

            if (feature) {
                vectorSource.removeFeature(feature);
                //console.log(`MapLayerServices: Feature "${featureId}" removed successfully.`);
            } else {
                console.warn(`MapLayerServices: Feature "${featureId}" not found in vector layer.`);
            }
        } else {
            console.warn('MapLayerServices: Vector layer "vector-parcelle" not found on the map.');
        }
    }
    /**
    * Removes a single layer from the map by name.
    * @param {Object} mapRef - The OpenLayers map reference.
    * @param {string} layerName - The name of the layer to remove.
    */
    removeLayer(mapRef, layerName) {
        //console.log(`Attempting to remove layer: ${layerName}`);
        const layer = mapRef.getLayers().getArray().find(l => l.get('name') === layerName);

        if (layer) {
            mapRef.removeLayer(layer);
            //console.log(`Layer "${layerName}" removed successfully.`);
        } else {
            console.warn(`Layer "${layerName}" not found on the map.`);
        }
    }


    /**
     * Removes a single layer from the map by name.
     * @param {string} layer - The name of the layer to remove.
     */
    removeOneLayer(layer) {
        let refMap = $('#map').data('map');
        refMap.getLayers().getArray().forEach(item => {
            if (item.get('name') === layer) {
                refMap.removeLayer(item);
                //console.log(`Layer "${layer}" removed via removeOneLayer method.`);
            }
        });
    }

    /**
     * Clears all layers except for 'cadastre:parcelles'.
     */
    clearLayerButParcelle() {
        let refMap = $('#map').data('map');
        let layers = [...refMap.getLayers().getArray()];
        layers.forEach(item => {
            if (item.get('name') !== "cadastre:parcelles" && item.get('name').includes(":")) {
                refMap.removeLayer(item);
                //console.log(`Layer "${item.get('name')}" removed by clearLayerButParcelle method.`);
            }
        });
    }

    /**
     * Cleans heading layers by removing specific layers from the map.
     * @param {string} refHeading - The name of the heading layer to clean.
     */
    cleanHeadingLayers(refHeading) {
        let refMap = $('#map').data('map');
        let layersMapRef = refMap.getLayers().getArray();
        let filteredLayers = layersMapRef.filter(layer => layer.get('name') === refHeading);
        filteredLayers.forEach(layer => refMap.removeLayer(layer));
        MainStore.dispatch({ type: 'STOCK_HEADING_LAYERS_REFS', data: { layersInHeading: [] } });
        //console.log(`Heading layers cleaned: "${refHeading}".`);
    }

    /**
     * Adds layers to the map after retrieving the necessary entity.
     * @param {Object} refMap - The map reference.
     * @param {Array} ref - The layer references to add.
     */
    async addLayer(refMap, ref) {
        try {
            const entity = await this.getEntity();
            if (!entity) {
                throw new Error("Entity data is unavailable.");
            }
            await this.processLayerWithEntity(entity, refMap, ref);
        } catch (error) {
            await this.processLayerWithOutEntity(refMap, ref);

        }
    }

    /**
     * Retrieves the entity either from the Redux store or via an API call if codes_terr is absent.
     * @returns {Object|null} The entity object or null if retrieval fails.
     */
    async getEntity() {
        let entity = MainStore.getState().MeReducer.entity;

        if (!entity?.codes_terr) {
            try {
                const result = await axios.get("/Api/user");
                if (result.data && result.data.entity) {
                    entity = result.data.entity;
                    // Optionally, update the Redux store if needed
                    // MainStore.dispatch({ type: 'UPDATE_ENTITY', payload: result.data.entity });
                } else {
                    console.error("Entity data not found in API response.");
                    return null;
                }
            } catch (error) {
                console.error("Error fetching entity:", error);
                Swal.fire({
                    icon: 'error',
                    text: "Impossible de récupérer les informations utilisateur.",
                    title: 'Erreur'
                });
                return null;
            }
        }

        return entity;
    }





    
        /**
     * Processes layers with the provided entity.
     * @param {Object} entity - The user entity.
     * @param {Object} refMap - The map reference.
     * @param {Array} ref - The layer references.
     */
async processLayerWithEntity(entity, refMap, layerNames) {
    await this.loadWMTSCapabilities();
    const userInsee = entity.codes_terr ? JSON.parse(entity.codes_terr) : [];
    const userLevelTerr = (entity.level_terr || '').trim();
    let userCodeInsee = '';
    if (userInsee.length && userLevelTerr !== 'dep') {
      userCodeInsee = userInsee.map(i => i.code).join("','");
    }

    for (const name of layerNames) {
      console.log('Processing layer', name, 'WebGL?', this.useWebGL);

      // 1) on prépare la source (WMTS ou TileWMS)
      const wmtsOpts = name !== 'cadastre:parcelles'
        ? this.getWMTSOptions(name)
        : null;

      const baseSource = wmtsOpts
        ? new WMTS(wmtsOpts)
        : new TileWMS({
            url: MainStore.getState().AppReducer.geoserverApiUrl,
            params: {
              LAYERS: name,
              TILED: true,
              FORMAT: 'image/png',
              VERSION: '1.1.1'
            },
            serverType: 'geoserver',
            crossOrigin: 'Anonymous'
          });

      // 2) on crée le layer GPU ou fallback
      const layer = this.useWebGL
        ? new WebGLTile({ source: baseSource })
        : new TileLayer({
            source: baseSource,
            crossOrigin: 'Anonymous',
            opacity: 1.0,
            visible: true
          });

      layer.set('id', name);
      layer.set('name', name);
      layer.set('title', name);
      layer.set('displayInLayerSwitcher', false);
      layer.setZIndex(name === 'cadastre:parcelles' ? 10 : 100);

      // 3) on ajoute si pas déjà présent
      if (!refMap.getLayers().getArray().find(l => l.get('name') === name)) {
        refMap.addLayer(layer);
      } else {
        console.warn(`Layer "${name}" already exists.`);
        continue;
      }

      // 4) filtres CQL si nécessaire
      await this.applyCQLFilters(
        entity, layer, name,
        userCodeInsee, userLevelTerr, userInsee,
        name.startsWith('cadastre') ? 'commune' : 'id_insee'
      );

      // 5) interaction clic pour cadastre:parcelles
      if (name === 'cadastre:parcelles') {
        this.setupParcelleClickInteraction(refMap, baseSource);
      }

      // 6) gestion d’erreur
      layer.getSource().on('tileloaderror', e => {
        console.error(`Tile load error for "${name}":`, e);
      });
    }

    refMap.updateSize();
    refMap.render();
  }
/**
     * Processes layers for signed-out users.
     * @param {Object} refMap - The map reference.
     * @param {Array} ref - The layer references.
     */
  async processLayerWithOutEntity(refMap, layerNames) {
    await this.loadWMTSCapabilities();

    for (const name of layerNames) {
      console.log('Processing layer', name, 'WebGL?', this.useWebGL);

      const wmtsOpts = name !== 'cadastre:parcelles'
        ? this.getWMTSOptions(name)
        : null;

      const baseSource = wmtsOpts
        ? new WMTS(wmtsOpts)
        : new TileWMS({
            url: MainStore.getState().AppReducer.geoserverApiUrl,
            params: {
              LAYERS: name,
              TILED: true,
              FORMAT: 'image/png',
              VERSION: '1.1.1'
            },
            serverType: 'geoserver',
            crossOrigin: 'Anonymous'
          });

      const layer = this.useWebGL
        ? new WebGLTile({ source: baseSource })
        : new TileLayer({
            source: baseSource,
            crossOrigin: 'Anonymous',
            opacity: 1.0,
            visible: true
          });

      layer.set('id', name);
      layer.set('name', name);
      layer.set('title', name);
      layer.set('displayInLayerSwitcher', false);

      if (!refMap.getLayers().getArray().find(l => l.get('name') === name)) {
        refMap.addLayer(layer);
      } else {
        console.warn(`Layer "${name}" already exists.`);
        continue;
      }

      if (name === 'cadastre:parcelles') {
        this.setupParcelleClickInteraction(refMap, baseSource);
      }

      layer.getSource().on('tileloaderror', e => {
        console.error(`Tile load error for "${name}":`, e);
      });
    }

    refMap.updateSize();
    refMap.render();
  }

    /**
    * Applies CQL filters to the layer based on entity data.
    * @param {Object} entity - The user entity.
    * @param {TileLayer} newLayer - The new layer to apply filters to.
    * @param {string} item - The name of the current layer.
    * @param {string} userCodeInsee - The user code INSEE.
    * @param {string} userLevelTerr - The user's territorial level.
    * @param {Array} userInsee - Array of user INSEE codes.
    * @param {string} cqlKey - The key to use in the CQL filter.
    */
    async applyCQLFilters(entity, newLayer, item, userCodeInsee, userLevelTerr, userInsee, cqlKey) {
        let testParams = newLayer.getSource().getParams();

        // Fetch filtered cadastre layers
        const dynamicCadastreLayers = await this.fetchFilteredLayers();

        // Check if the current layer needs to be filtered
        if (dynamicCadastreLayers.includes(item)) {
            // Now use cqlKey in the CQL filter expressions
            if (userLevelTerr?.trim() === 'epci') {
                try {
                    const result = await axios.get(`/Api/geodata/epci_comm/${userCodeInsee}`);
                    let codes_coms = result.data[0].codes_com;
                    // Assuming codes_coms is an array, adjust if it's a string
                    if (Array.isArray(codes_coms)) {
                        testParams.cql_filter = `${cqlKey} IN ('${codes_coms.join("','")}')`;
                    } else {
                        testParams.cql_filter = `${cqlKey} IN ('${codes_coms}')`;
                    }
                } catch (err) {
                    console.error(err);
                }
            } else if (userLevelTerr?.trim() === 'dep') {
                testParams.cql_filter = userInsee.map((codeRef) => {
                    return `${cqlKey} LIKE '${codeRef.code}%'`;
                }).join(' OR ');
            } else if (userLevelTerr?.trim() === 'reg') {
                testParams.cql_filter = `${cqlKey} LIKE '2A%' OR ${cqlKey} LIKE '2B%'`;
            } else {
                // Handle multiple userCodeInsee values separated by ";"
                if (userCodeInsee && userCodeInsee.includes(';')) {
                    // Split the userCodeInsee string by ";" and trim each code
                    const userCodesArray = userCodeInsee.split(';').map(code => code.trim()).filter(code => code !== '');

                    if (userCodesArray.length > 0) {
                        // Escape single quotes in codes to prevent CQL injection
                        const escapedUserCodes = userCodesArray.map(code => code.replace(/'/g, "\\'"));
                        testParams.cql_filter = `${cqlKey} IN ('${escapedUserCodes.join("','")}')`;
                    } else {
                        // Handle case where no valid codes are present after splitting
                        testParams.cql_filter = `${cqlKey} IN ('${userCodeInsee}')`;
                    }
                } else if (userCodeInsee) {
                    // Single code, no need to split
                    testParams.cql_filter = `${cqlKey} IN ('${userCodeInsee}')`;
                }
            }

            newLayer.getSource().updateParams(testParams);
        } else {
            console.log(`No CQL Filter applied to layer "${newLayer.get('name')}" as it's not marked as filtered.`);
        }
    }


    /**
     * Sets up the click interaction for parcelle layers.
     * @param {Object} refMap - The map reference.
     * @param {Object} parcelleSource - The source for parcelle layers.
     */
    setupParcelleClickInteraction(refMap, parcelleSource) {
        refMap.on('singleclick', async (evt) => {
            const currentMapInteractions = refMap.getInteractions().getArray();
            const isInDrawMode = currentMapInteractions.some(interaction => interaction.get('name'));

            if (!isInDrawMode) {
                const viewResolution = refMap.getView().getResolution();
                const url = CORSANYWHERE + parcelleSource.getFeatureInfoUrl(
                    evt.coordinate, viewResolution, 'EPSG:3857',
                    { 'INFO_FORMAT': 'application/json' }
                );

                if (url) {
                    Swal.fire({
                        html: '<p style="color:#FFFFFF;">Recherche ...</p>',
                        background: '#303a3f',
                        width: '15rem',
                        didOpen: () => {
                            Swal.showLoading();
                        }
                    });

                    try {
                        const response = await axios.get(url, {
                            headers: {
                                'Accept': 'application/json',
                                'Content-Type': 'application/json;charset=UTF-8'
                            },
                        });
                        Swal.close();
                        if (response.data.features) {
                            this.getFeatureInfo(response.data.features);
                        } else {
                            throw new Error("Aucune donnée retournée");
                        }
                    } catch (err) {
                        console.error(err);
                        Swal.close();
                        Swal.fire({
                            icon: 'error',
                            text: "Votre demande ne retourne aucune donnée",
                            title: 'Oups !'
                        });
                    }
                }
            }
        });
    }

    /**
    * Remplace la parcelle actuelle par une nouvelle et met à jour la couche vectorielle.
    * @param {Array} features - Les nouvelles parcelles à remplacer.
    */
    replaceParcelle(features) {
        const newParcelleCode = features[0].id.substring(10);
        const newParcelles = [newParcelleCode];
        MainStore.dispatch(replaceParcelles(newParcelles));

        // Préparer les nouvelles données GeoJSON
        const newFeaturesGeoJSON = {
            'type': 'FeatureCollection',
            'features': features
        };

        // Nettoyer la couche vectorielle avant d'ajouter les nouvelles données
        const mapGeoJsonService = new MapGeoJsonService();
        const refMap = $('#map').data('map'); // Récupère la référence de la carte
        mapGeoJsonService.clearVectorLayer();

        // Ajouter les nouvelles parcelles
        mapGeoJsonService.addGeoJsonLayer(refMap, newFeaturesGeoJSON, 'parcelle');

        MainStore.dispatch({
            type: 'UPDATE_VECTOR_LAYER',
            data: { geoJsonData: newFeaturesGeoJSON, typeTerr: 'parcelle' }
        });
    }

    /**
     * Gère la récupération des informations des caractéristiques et l'interaction utilisateur.
     * @param {Array} features - Le tableau des caractéristiques récupérées.
     */
    getFeatureInfo(features) {
        Swal.close();
        let currentParcelle = MainStore.getState().MapReducer.currentParcelle;
        const clickItem = features[0].id.substring(10);
        let isInList = _.findIndex(currentParcelle, (item) => {
            return clickItem === item;
        });

        if (isInList >= 0) {
            // La parcelle est déjà dans la liste ; la supprimer
            MainStore.dispatch(removeFeatureAction(clickItem)); // Définit vectoreRemoveFeature
            MainStore.dispatch({
                type: 'REMOVE_PARCELLE_FROM_LIST',
                data: { parcelleCode: clickItem },
            });
            // Réinitialiser vectoreRemoveFeature pour permettre de futures suppressions
            MainStore.dispatch(resetVectorRemoveFeatureAction());
        } else {
            if (currentParcelle.length === 0) {
                // Si la liste des parcelles est vide, ajouter directement sans popup
                MainStore.dispatch({
                    type: 'SHOW_CURRENT_SECTION',
                    data: 'data',
                });
                const newFeatures = {
                    'type': 'FeatureCollection',
                    'features': features
                };
                MainStore.dispatch({
                    type: 'UPDATE_VECTOR_LAYER',
                    data: { geoJsonData: newFeatures, typeTerr: 'parcelle' }
                });
                MainStore.dispatch({
                    type: 'ADD_PARCELLE_TO_LIST',
                    data: { parcelleCode: clickItem }
                });
                MainStore.dispatch({
                    type: 'SET_CURRENT_COM',
                    data: { currentCom: clickItem.substring(0, 5) }
                });
            } else {
                // Si la liste des parcelles n'est pas vide, afficher une popup avec les options Ajouter et Remplacer
                Swal.fire({
                    position: 'center',
                    width: '300px',
                    html: '<div class="text-left" style="color: var(--theme-color);"><div>Parcelle :</div><h4 class="font-weight-bold">' + clickItem + '</h4></div>',
                    showCancelButton: false,
                    showDenyButton: true, // Ajoute un bouton Denié pour l'option Remplacer
                    showCloseButton: true,
                    confirmButtonText: 'Ajouter à ma sélection', // Bouton Ajouter
                    denyButtonText: 'Remplacer la sélection', // Bouton Remplacer
                    cancelButtonText: 'Annuler',
                    buttonsStyling: false,
                    reverseButtons: true,
                    customClass: {
                        denyButton: 'btn btn-lg btn-primary text-white mb-3 btn-block',
                        cancelButton: 'btn btn-lg btn-warning text-white mb-3 btn-block', // Style pour le bouton Remplacer
                        confirmButton: 'btn btn-lg btn-outline-primary btn-block ',
                        content: "ueThemeBackground"
                    },
                }).then((result) => {
                    if (result.isConfirmed) {
                        // L'utilisateur a choisi d'ajouter la parcelle
                        MainStore.dispatch({
                            type: 'SHOW_CURRENT_SECTION',
                            data: 'data',
                        });
                        const newFeatures = {
                            'type': 'FeatureCollection',
                            'features': features
                        };
                        MainStore.dispatch({
                            type: 'UPDATE_VECTOR_LAYER',
                            data: { geoJsonData: newFeatures, typeTerr: 'parcelle' }
                        });
                        MainStore.dispatch({
                            type: 'ADD_PARCELLE_TO_LIST',
                            data: { parcelleCode: clickItem }
                        });
                        MainStore.dispatch({
                            type: 'SET_CURRENT_COM',
                            data: { currentCom: clickItem.substring(0, 5) }
                        });
                    } else if (result.isDenied) {
                        // L'utilisateur a choisi de remplacer la parcelle
                        // Remplacer les parcelles de manière atomique
                        this.replaceParcelle(features);
                        MainStore.dispatch({
                            type: 'SHOW_CURRENT_SECTION',
                            data: 'data',
                        });

                    }
                    // Si annulé, ne rien faire
                });
            }
        }
    }
}

export default MapLayerServices;
